import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { MonoTypeOperatorFunction, Observable, map, tap } from 'rxjs';

import {
  DeletePlanEntitiesByFilterRequest,
  DeletePlanEntitiesRequest
} from '@gfe/core/models/entity/delete-entity-request.model';
import {
  CreatePlanEntitiesBatchRequest,
  CreatePlanEntityRequest,
  UpdatePlanEntitiesBatchRequest,
  UpdatePlanEntityRequest
} from '@gfe/core/models/entity/entity-selector-request.model';
import {
  PlanFindingsResponse,
  PlanFindingsWire,
  findingsFromWire
} from '@gfe/core/models/plan-finding.model';
import { PlanMetadataModel, PlanModel } from '@gfe/core/models/plan.model';
import { AddPlanningSpaceResponse } from '@gfe/core/models/planning-space/planning-space.model';
import { DataTransformerSuccessResponse } from '@gfe/core/models/transformers.model';
import { PlanFindingsActions } from '@gfe/core/store/plan-findings/plan-findings.actions';
import { GBE_BASE_URL } from '@gfe/core/tokens/gbe-url.token';
import { convertKeysToSnakeCase } from '@gfe/core/utils/case-conversions';
import { FilterConfiguration } from '@gfe/shared/components/filter-query-builder/filter.models';
import { convertFilterConfigurationToBackEndFormat } from '@gfe/shared/utils/search-filter.utils';

import { FilterExpr } from '@core/filters/path-filter/wire';
import {
  SKIP_GBE_ERROR_NOTIFICATION,
  SKIP_GBE_ERROR_RETRY
} from '@core/interceptors/gbe-error.interceptor';
import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';

/**
 * Written snake_case by hand: `apiCaseRequestInterceptor` rewrites the request body only, never
 * query params, so a camelCase spelling would reach GBE verbatim, be ignored, and restore the
 * slow response with nothing to show for it.
 */
const FINDINGS_EXCLUDED_PARAMS = new HttpParams({
  fromObject: { exclude_findings: true }
});

/**
 * The findings routes return findings and nothing else, so dropping the deprecated duplicate of
 * the list halves the whole response.
 */
const FINDINGS_COMPACT_PARAMS = new HttpParams({
  fromObject: { exclude_deprecated_fields: true }
});

export interface FetchOnePlanRequest {
  planId: string;
}
export interface FetchOnePlanResponse {
  plan: PlanModel;
}

export interface AddToPlanRequest {
  entityType: string;
  planId: string; // plan id
  uids: string[]; // user ids
}

export interface AddToPlanByFilter {
  entityType: string;
  planId: string;
  filters?: FilterConfiguration;
  search?: string;
  pathFilter?: FilterExpr;
}

export interface AddToPlanResponse {
  plan: PlanModel;
}

export interface DeletePlansRequest {
  planIds: string[];
}

export interface DuplicatePlanRequest {
  planId: string;
  name?: string;
  description?: string;
}

/** A field left out is left unchanged by GBE. */
export interface UpdatePlanRequest {
  planId: string;
  name?: string;
  description?: string;
}

export interface CheckPlanRequest {
  planId: string;
  paramId: number;
}

@Injectable({
  providedIn: 'root'
})
export class PlansService {
  private readonly http = inject(HttpClient);
  private readonly gbeUrl = inject(GBE_BASE_URL);
  private readonly store = inject(Store);

  private buildUrl(endpoint: string): string {
    return `${this.gbeUrl}/${endpoint}`;
  }

  /**
   * Findings describe the plan's data, so any write invalidates them. Announced here
   * rather than at the call sites: the writes fan out across components, most of them
   * on plan sub-routes, and a missed one silently leaves a stale panel reading current.
   */
  private announcePlanEdit<T>(planId: string): MonoTypeOperatorFunction<T> {
    return tap<T>(() =>
      this.store.dispatch(PlanFindingsActions.planDataChanged({ planId }))
    );
  }

  constructor() {}

  /**
   * Asks for the plan without its findings: `PlanMetadataModel` declares none, and the panel
   * reads them from the findings endpoint instead. On a plan with many of them the excluded
   * fields are effectively the whole response, and the backend skips the call that builds them.
   *
   * `skipErrorNotification` is for a background lookup whose caller copes without the plan.
   */
  public fetchOne$(
    request: FetchOnePlanRequest,
    options?: { skipErrorNotification?: boolean }
  ): Observable<PlanMetadataModel> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/${request.planId}`);
    return this.http
      .get<PlanMetadataModel>(url, {
        params: FINDINGS_EXCLUDED_PARAMS,
        context: options?.skipErrorNotification
          ? new HttpContext().set(SKIP_GBE_ERROR_NOTIFICATION, true)
          : undefined
      })
      .pipe(
        map((result) => {
          return {
            ...result,
            nbEntitiesPerType: convertKeysToSnakeCase(result.nbEntitiesPerType)
          };
        })
      );
  }

  public addToPlan$(request: AddToPlanRequest): Observable<AddToPlanResponse> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/data`);
    return this.http
      .post<AddToPlanResponse>(url, {
        entityType: request.entityType.toUpperCase(),
        planId: request.planId,
        uids: request.uids
      })
      .pipe(this.announcePlanEdit<AddToPlanResponse>(request.planId));
  }

  public addToPlanByFilter$(request: AddToPlanByFilter): Observable<AddToPlanResponse> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/data/by-filter`);

    // Convert filters to backend format before sending
    const requestWithBackendFilters = {
      ...request,
      entityType: request.entityType.toUpperCase(),
      filters: convertFilterConfigurationToBackEndFormat(request.filters)
    };

    return this.http
      .post<AddToPlanResponse>(url, requestWithBackendFilters)
      .pipe(this.announcePlanEdit<AddToPlanResponse>(request.planId));
  }

  /**
   * Always the batch route, even for a single plan: the contract is identical (same
   * refusal codes, same all-or-nothing child-plan check, same empty 204), so a second
   * single-plan path would only duplicate the caller's error mapping.
   *
   * All-or-nothing: the whole set is judged at once and a refusal deletes nothing, on
   * either side. It also reports every offending plan rather than the first, so a
   * caller mapping codes to copy must expect a `multiple_errors` envelope.
   *
   * The generic error notification is off because the copy GBE returns for
   * `plan_already_deployed` is shared with the edit paths and says "can't be modified",
   * which is wrong for a delete — the caller maps the code to its own message. Retries
   * are off because the call is not idempotent and each attempt costs a data-layer
   * round trip of up to 25 s per plan.
   */
  public deletePlans$(request: DeletePlansRequest): Observable<void> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/batch`);
    return this.http.delete<void>(url, {
      // `apiCaseRequestInterceptor` snake_cases the body, so this reaches GBE as the
      // `plan_ids` the route expects.
      body: { planIds: request.planIds },
      context: new HttpContext()
        .set(SKIP_GBE_ERROR_NOTIFICATION, true)
        .set(SKIP_GBE_ERROR_RETRY, true)
    });
  }

  public duplicatePlan$(
    request: DuplicatePlanRequest
  ): Observable<AddPlanningSpaceResponse> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/${request.planId}/duplicate`);
    return this.http.post<AddPlanningSpaceResponse>(url, {
      name: request.name,
      description: request.description
    });
  }

  /**
   * Name and description only: they are plan metadata, not plan data, so the findings stay
   * current and no edit is announced.
   */
  public updatePlan$(request: UpdatePlanRequest): Observable<void> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/${request.planId}`);
    return this.http.patch<void>(url, {
      name: request.name,
      description: request.description
    });
  }

  /** Runs a fresh check and stores it. The response is never stale. */
  public checkPlan$(request: CheckPlanRequest): Observable<PlanFindingsResponse> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/check`);
    return this.http
      .post<PlanFindingsWire>(url, request, { params: FINDINGS_COMPACT_PARAMS })
      .pipe(map(findingsFromWire));
  }

  /** Reads the stored findings. No plan load, no stored run — cheap enough to poll. */
  public getPlanFindings$(request: CheckPlanRequest): Observable<PlanFindingsResponse> {
    const url = this.buildUrl(
      `${GbeEndPointV4.PLANS}/check/${request.planId}/param/${request.paramId}`
    );
    return this.http
      .get<PlanFindingsWire>(url, { params: FINDINGS_COMPACT_PARAMS })
      .pipe(map(findingsFromWire));
  }

  /**
   * Creates one record that lives in the plan alone — global data is left untouched. The
   * response carries the uid, which the caller may not know: a create can leave it blank
   * and let the backend mint one.
   */
  public createEntityData$(
    planId: string,
    entityType: string,
    request: CreatePlanEntityRequest
  ): Observable<DataTransformerSuccessResponse> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/${planId}/data/${entityType}`);
    return this.http
      .post<DataTransformerSuccessResponse>(url, request)
      .pipe(this.announcePlanEdit<DataTransformerSuccessResponse>(planId));
  }

  /**
   * The CSV import route: one request, one transaction, all rows or none. Rows already in
   * the plan are re-versioned rather than duplicated, which is what makes export → edit →
   * re-import a bulk edit instead of a bulk insert.
   *
   * `skipErrorNotification` is for a caller that renders the per-row findings itself; the
   * interceptor's generic notification would otherwise repeat them at unreadable length.
   */
  public createEntitiesBatch$(
    planId: string,
    entityType: string,
    request: CreatePlanEntitiesBatchRequest,
    options?: { skipErrorNotification?: boolean }
  ): Observable<DataTransformerSuccessResponse> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/${planId}/data/${entityType}/batch`);
    return this.http
      .post<DataTransformerSuccessResponse>(url, request, {
        context: options?.skipErrorNotification
          ? new HttpContext().set(SKIP_GBE_ERROR_NOTIFICATION, true)
          : undefined
      })
      .pipe(this.announcePlanEdit<DataTransformerSuccessResponse>(planId));
  }

  public updateEntityData$(
    planId: string,
    entityType: string,
    request: UpdatePlanEntityRequest
  ): Observable<void> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/${planId}/data/${entityType}`);
    return this.http.patch<void>(url, request).pipe(this.announcePlanEdit<void>(planId));
  }

  /**
   * Merge-patches many entities of one type at once. The route applies the whole set or none
   * of it, so a caller never has to work out which half of a selection moved, and it counts
   * as a single plan edit rather than one per entity.
   */
  public patchEntitiesBatch$(
    planId: string,
    entityType: string,
    request: UpdatePlanEntitiesBatchRequest
  ): Observable<void> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/${planId}/data/${entityType}/batch`);
    return this.http.patch<void>(url, request).pipe(this.announcePlanEdit<void>(planId));
  }

  public deleteEntities$(
    planId: string,
    request: DeletePlanEntitiesRequest
  ): Observable<void> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/${planId}/data`);
    return this.http
      .delete<void>(url, { body: request })
      .pipe(this.announcePlanEdit<void>(planId));
  }

  public deleteEntitiesByFilter$(
    planId: string,
    request: DeletePlanEntitiesByFilterRequest
  ): Observable<void> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/${planId}/data/by-filter`);

    // Convert filters to backend format before sending
    const requestWithBackendFilters = {
      ...request,
      filters: convertFilterConfigurationToBackEndFormat(request.filters)
    };

    return this.http
      .delete<void>(url, { body: requestWithBackendFilters })
      .pipe(this.announcePlanEdit<void>(planId));
  }

  /**
   * Deploys one configuration. `202` with no body: the deploy runs on a queue and reports
   * through the existing websocket status stream. Omitting `uids` deploys every message,
   * which is also the only accepted shape for a configuration that cannot deploy in steps.
   */
  public deployConfig$(
    planId: string,
    configId: string,
    uids?: string[]
  ): Observable<void> {
    const url = this.buildUrl(`${GbeEndPointV4.PLANS}/${planId}/deploy/${configId}`);
    return this.http.post<void>(url, uids === undefined ? {} : { uids });
  }
}
